import { useQuery as useZeroQuery } from '@rocicorp/zero/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useMemo } from 'react';

import { mergeabilityQuery } from '@/features/pulls/services/pulls';
import { TERMINAL_REVIEW_STATES } from '@/hooks/use-reviewers';
import { useZero } from '@/lib/zero';
import { useUser } from '@/services/user';

export function usePullStatus() {
  const { owner, repo, number } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });
  const { user } = useUser();

  const z = useZero();
  const [pull, { type }] = useZeroQuery(
    z.query.PullsTable.where(({ and, cmp }) =>
      and(cmp('owner', owner), cmp('repo', repo), cmp('number', Number(number)))
    ).one()
  );

  const [reviews] = useZeroQuery(
    z.query.ReviewTable.where(({ and, cmp }) =>
      and(cmp('owner', owner), cmp('repo', repo), cmp('pullNumber', Number(number)))
    )
  );

  // Get reviewers and their last review state
  const reviewers: { login: string; avatarUrl: string; lastReviewState: string }[] = useMemo(() => {
    if (!reviews) return [];

    const grouped = Object.groupBy(
      reviews.filter((r) => r !== null),
      (r) => r.author!.login
    );

    return Object.values(grouped)
      .filter((r) => r && r.length > 0)
      .map((r) => {
        const sorted = r!.sort((a, b) => {
          const aTerminal = TERMINAL_REVIEW_STATES.includes(a.state);
          const bTerminal = TERMINAL_REVIEW_STATES.includes(b.state);
          if (aTerminal && !bTerminal) return -1;
          if (!aTerminal && bTerminal) return 1;
          return Date.parse(b!.createdAt ?? '') - Date.parse(a!.createdAt ?? '');
        });

        return {
          login: sorted[0]!.author!.login,
          avatarUrl: sorted[0]!.author!.avatarUrl,
          lastReviewState: sorted[0]!.state,
        };
      });
  }, [reviews]);

  const isApproved = useMemo(
    () =>
      !reviewers.some((r) => r.lastReviewState === 'CHANGES_REQUESTED') &&
      reviewers.some((r) => r.lastReviewState === 'APPROVED'),
    [reviewers]
  );
  const isAuthor = useMemo(() => pull?.author.login === user.login, [pull, user]);
  const alreadyReviewed = useMemo(
    () =>
      reviews?.some(
        (review) =>
          review?.author?.login === user.login && review?.state && TERMINAL_REVIEW_STATES.includes(review.state)
      ),
    [reviews, user]
  );

  // Improved mergeability polling with smart intervals and proper cleanup
  const {
    data: mergeability,
    isLoading: isLoadingMergeability,
    error,
  } = useQuery(mergeabilityQuery(owner, repo, number, pull?.state));

  return {
    state: pull?.state,
    hasConflicts: mergeability ? mergeability === 'CONFLICTING' : undefined,
    isApproved,
    isAuthor,
    alreadyReviewed,
    checksPassing: pull?.checkStatusRollup === 'SUCCESS',
    pull,
    mergeabilityError: error,
  };
}
